// "Ask the one question" (Marketing Hub, build spec screen 25): the answers a
// family can tap, how long a link lives, and the answer buttons an email
// carries.
//
// CONSUMER: shared (the Today email route, the public answer route, the
// public answer page, tests).
//
// THE LINK IS THE CREDENTIAL. A family taps an answer in an email and lands
// on a page that needs no sign-in, the same as a booking or intake link. So
// everything that can go wrong with a link someone else holds is decided
// here, in one place both the server and the page read:
//   * a link names ONE household, ONE company and ONE Services Check cell,
//     and a tap can write only that cell;
//   * a link lives LINK_LIFE_DAYS and then answers nothing;
//   * the first answer can be changed once within CHANGE_WINDOW_HOURS (a
//     mis-tap on a phone), then the link is spent;
//   * opening a link never writes. Mail-security scanners open every link
//     in an email, so an answer is written only by the page's own button
//     press (a POST), never by arriving at the page.
//
// "Not sure" is not an answer to store. It writes nothing on the map and
// sends nothing; the advisor gets a task to bring it up on the next call
// (spec: "'Not sure' schedules a note for the next call, never a second
// email").
//
// No database and no React here.

import type { ServiceMapStoredState } from "./service-map";

export const QUESTION_ACTION = "ask_one_question";

export function rowAsksQuestion(action: string): boolean {
  return action === QUESTION_ACTION;
}

/** What a family can tap. Each stored answer maps to a Services Check state;
 *  "not_sure" stores nothing. Labels are the family's words, not ours. */
export const ANSWER_CHOICES = [
  { key: "has_it_elsewhere", label: "We already have it elsewhere" },
  { key: "interested", label: "I'd like to hear more" },
  { key: "not_right_now", label: "Not right now" },
  { key: "not_applicable", label: "It doesn't apply to us" },
  { key: "do_not_ask", label: "Please don't ask about this again" },
  { key: "not_sure", label: "I'm not sure" },
] as const;

export type AnswerChoice = (typeof ANSWER_CHOICES)[number]["key"];

export function isAnswerChoice(value: unknown): value is AnswerChoice {
  return typeof value === "string" && ANSWER_CHOICES.some((c) => c.key === value);
}

/** The Services Check state a choice writes, or null for "not sure". */
export function storedStateFor(choice: AnswerChoice): ServiceMapStoredState | null {
  return choice === "not_sure" ? null : choice;
}

export function choiceLabel(choice: string): string {
  return ANSWER_CHOICES.find((c) => c.key === choice)?.label ?? choice;
}

/** How long a link answers. Long enough for a family to get to their inbox. */
export const LINK_LIFE_DAYS = 30;
/** How long after the first tap the answer can still be changed. */
export const CHANGE_WINDOW_HOURS = 24;
/** The first answer plus one change. */
export const MAX_ANSWERS = 2;
/** "Not right now" comes back around after this many months. */
export const NOT_RIGHT_NOW_MONTHS = 6;

/** YYYY-MM, `months` after the month of `nowIso`. */
export function monthsAhead(nowIso: string, months: number): string {
  const d = new Date(`${nowIso.slice(0, 7)}-01T00:00:00.000Z`);
  d.setUTCMonth(d.getUTCMonth() + months);
  return d.toISOString().slice(0, 7);
}

/** Where a link stands, in the order the server checks it. */
export type LinkRefusal = "not_found" | "expired" | "revoked" | "spent" | "opted_out" | "gone";

/** Plain words for a family on why a link no longer works. */
export const REFUSAL_MESSAGE: Record<LinkRefusal, string> = {
  not_found: "This link doesn't work. It may have been copied incompletely.",
  expired: "This question has closed. If you'd still like to tell us, just reply to the email.",
  revoked: "This question was withdrawn. No need to answer it.",
  spent: "Thanks, we already have your answer.",
  opted_out: "You've asked us not to email you, so this link was turned off.",
  gone: "This link no longer works.",
};

/**
 * Whether a link can take an answer now. `answerCount` and `firstAnsweredAt`
 * are what the link has recorded so far. Pure, so the page and the server
 * agree on "can I still change this?".
 */
export function linkOpenFor(
  link: { expires_at: string; revoked_at: string | null; answer_count: number; first_answered_at: string | null },
  nowIso: string,
): { ok: true; canChange: boolean } | { ok: false; reason: LinkRefusal } {
  if (link.revoked_at) return { ok: false, reason: "revoked" };
  if (link.expires_at <= nowIso) return { ok: false, reason: "expired" };
  if (link.answer_count >= MAX_ANSWERS) return { ok: false, reason: "spent" };
  if (link.answer_count > 0 && link.first_answered_at) {
    const closes = new Date(Date.parse(link.first_answered_at) + CHANGE_WINDOW_HOURS * 3_600_000).toISOString();
    if (closes <= nowIso) return { ok: false, reason: "spent" };
  }
  return { ok: true, canChange: link.answer_count > 0 };
}

/** The page a family lands on. `choice` pre-selects the answer they tapped. */
export function answerUrl(origin: string, rawToken: string, choice?: AnswerChoice): string {
  const base = `${origin}/q/${encodeURIComponent(rawToken)}`;
  return choice ? `${base}?a=${choice}` : base;
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

/** The answer buttons an email carries: one link per choice. */
export function answerButtonsHtml(origin: string, rawToken: string): string {
  const links = ANSWER_CHOICES.map(
    (c) =>
      `<a href="${escapeHtml(answerUrl(origin, rawToken, c.key))}" style="display:inline-block;margin:4px 6px 4px 0;padding:10px 14px;border:1px solid #4e98c6;border-radius:6px;color:#022c51;text-decoration:none;font-weight:600">${escapeHtml(c.label)}</a>`,
  );
  return `<p>${links.join("")}</p>`;
}

/** Where the person typing the email wants the buttons. If they took the
 *  marker out, the buttons go under the message, above the signature. */
export const ANSWER_BUTTONS_TOKEN = "{{answer_buttons}}";

/**
 * The task a "Not sure" tap leaves the advisor who asked. Wording resolves
 * against communication_templates by this slug (automation-first rule 2);
 * the text here is the fallback only while that row is missing. Subject is
 * the task's title, body its description. {{household}} and {{product}}
 * fill in.
 */
export const NOT_SURE_TASK = {
  slug: "nbh-task-not-sure",
  subject: "{{household}}: bring up {{product}} on your next call",
  body: "Asked by email whether they have {{product}}, they tapped \"I'm not sure\". Nothing was written on their Services Check. Ask on your next call; no second email.",
} as const;
